/**
 * Ticket validation page: QR scanner + manual search.
 * Used at event entrance to validate and mark tickets.
 *
 * When embedded in AdminOrdersPage, uses shared event context
 * to show warnings when scanned ticket is from a different event.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import {
  IconCamera,
  IconSearch,
  IconTicket,
  IconAlertTriangle,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic, safeShowAlert } from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import type { TicketValidationResult } from "../shared/types.ts";
import { QRScanner } from "./components/QRScanner.tsx";
import { ValidationResult } from "./components/ValidationResult.tsx";
import { useAdminOrdersContext } from "../orders/context/index.ts";
import { useValidateTicket, useMarkTicketUsed } from "./hooks";

type ViewMode = "idle" | "scanning" | "result";

export interface TicketValidationPageProps {
  /** When true, render without Layout and use onClose instead of BackButton */
  embedded?: boolean;
  /** Called when user closes the embedded view */
  onClose?: () => void;
}

export function TicketValidationPage({
  embedded = false,
  onClose: _onClose,
}: TicketValidationPageProps = {}) {
  // Note: _onClose is part of the embedded interface but not currently used
  void _onClose;
  const navigate = useNavigate();
  const tg = getTelegram();

  // Try to get event from context (when embedded)
  let contextEventId: string | null = null;
  let contextEventTitle: string | null = null;
  try {
    const context = useAdminOrdersContext();
    contextEventId = context.selectedEventId;
    contextEventTitle = context.selectedEvent?.title ?? null;
  } catch {
    // Not in context - standalone mode
  }

  const [viewMode, setViewMode] = useState<ViewMode>("idle");
  const [searchQuery, setSearchQuery] = useState("");
  const [validationResult, setValidationResult] =
    useState<TicketValidationResult | null>(null);

  // URQL hooks
  const { validateTicket: validateTicketMutation, isLoading } =
    useValidateTicket();
  const { markTicketUsed: markTicketUsedMutation, isLoading: isProcessing } =
    useMarkTicketUsed();

  // Setup BackButton (only when not embedded)
  useEffect(() => {
    if (!tg || embedded) return;

    tg.BackButton.show();
    const handleBack = () => {
      if (viewMode === "scanning") {
        setViewMode("idle");
      } else if (viewMode === "result") {
        setValidationResult(null);
        setViewMode("idle");
      } else {
        navigate("/admin/tickets");
      }
    };
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, viewMode, embedded]);

  // Validate ticket by number
  const validateTicket = useCallback(
    async (ticketNumber: string) => {
      setValidationResult(null);

      try {
        const result = await validateTicketMutation(ticketNumber);
        setValidationResult(result);
        setViewMode("result");

        // Haptic feedback based on result
        if (result.isValid) {
          haptic("success");
        } else if (result.alreadyUsedAt) {
          haptic("warning");
        } else {
          haptic("error");
        }
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка валидации"));
      }
    },
    [validateTicketMutation]
  );

  // Handle QR scan
  const handleScan = useCallback(
    (data: string) => {
      setViewMode("idle");
      // Extract ticket number from QR data
      // QR might contain full URL or just ticket number
      const ticketNumber = data.includes("/")
        ? (data.split("/").pop() ?? data)
        : data;
      validateTicket(ticketNumber);
    },
    [validateTicket],
  );

  // Handle manual search
  const handleSearch = useCallback(() => {
    if (!searchQuery.trim()) return;
    validateTicket(searchQuery);
  }, [searchQuery, validateTicket]);

  // Mark ticket as used
  const handleMarkUsed = useCallback(async () => {
    if (!validationResult?.ticket?.id) return;

    try {
      await markTicketUsedMutation(validationResult.ticket.id);
      haptic("success");
      safeShowAlert("Билет отмечен как использованный");
      // Update result to show as used
      setValidationResult({
        ...validationResult,
        isValid: false,
        alreadyUsedAt: new Date().toISOString(),
        message: "Билет успешно отмечен",
      });
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка отметки"));
    }
  }, [validationResult, markTicketUsedMutation]);

  // Scan another ticket
  const handleScanAnother = useCallback(() => {
    setValidationResult(null);
    setSearchQuery("");
    setViewMode("idle");
  }, []);

  // Start QR scanner
  const startScanner = useCallback(() => {
    haptic("light");
    setViewMode("scanning");
  }, []);

  // Check if ticket is from a different event
  const isDifferentEvent =
    embedded &&
    contextEventId &&
    validationResult?.event?.id &&
    validationResult.event.id !== contextEventId;

  const Wrapper = embedded ? "div" : Layout;

  return (
    <>
      <Wrapper {...(!embedded ? { showNav: viewMode !== "scanning" } : {})}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center text-teal-600">
            <IconTicket className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Валидация</h1>
            <p className="text-[13px] text-slate-500">
              {embedded && contextEventTitle
                ? `Проверка билетов: ${contextEventTitle}`
                : "Проверка билетов на входе"}
            </p>
          </div>
        </div>

        {/* Different event warning */}
        {isDifferentEvent && validationResult?.event && (
          <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <IconAlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-amber-800">
                  Билет от другого события
                </p>
                <p className="text-[12px] text-amber-700 mt-0.5">
                  Этот билет для события "{validationResult.event.title}", а не
                  для "{contextEventTitle}"
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Result view */}
        {viewMode === "result" && validationResult && (
          <ValidationResult
            result={validationResult}
            onMarkUsed={validationResult.isValid ? handleMarkUsed : undefined}
            onScanAnother={handleScanAnother}
            isProcessing={isProcessing}
          />
        )}

        {/* Idle view: search and scan options */}
        {viewMode === "idle" && (
          <>
            {/* Manual search */}
            <div className="card p-4 mb-4">
              <p className="text-[13px] font-medium text-slate-700 mb-3">
                Поиск по номеру билета
              </p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Введите номер билета"
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-slate-100 border-0 text-[16px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
                    autoComplete="off"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={!searchQuery.trim() || isLoading}
                  className="px-5 py-3 rounded-xl bg-[var(--color-accent)] text-white font-medium text-[15px] active:opacity-90 disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin block" />
                  ) : (
                    "Найти"
                  )}
                </button>
              </div>
            </div>

            {/* QR Scanner button */}
            <button
              type="button"
              onClick={startScanner}
              className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-teal-500 text-white font-medium text-[16px] shadow-sm active:opacity-90"
            >
              <IconCamera className="w-6 h-6" />
              Сканировать QR-код
            </button>

            {/* Instructions */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
              <p className="text-[13px] text-slate-600 leading-relaxed">
                <strong>Как использовать:</strong>
                <br />
                1. Нажмите "Сканировать QR-код" и наведите камеру на билет
                <br />
                2. Или введите номер билета вручную
                <br />
                3. Проверьте данные и нажмите "Отметить" для валидного билета
              </p>
            </div>
          </>
        )}

        {/* Bottom spacing */}
        <div className="h-4" />
      </Wrapper>

      {/* QR Scanner overlay */}
      <QRScanner
        isActive={viewMode === "scanning"}
        onScan={handleScan}
        onClose={() => setViewMode("idle")}
      />
    </>
  );
}
